export type ScanMethod = "QR" | "NFC" | "MANUAL";
export type ScanReceiptTone = "ok" | "pending" | "rejected";
export type ScanReceiptOutcome =
  | "validated"
  | "synced"
  | "queued-offline"
  | "queued-retry"
  | "flagged"
  | "duplicate"
  | "rejected";

export interface ScanReceipt {
  readonly tone: ScanReceiptTone;
  readonly title: string;
  readonly detail: string;
  readonly method: ScanMethod;
}

export function scanReceiptToneRole(tone: ScanReceiptTone): "accent" | "warn" | "error" {
  switch (tone) {
    case "ok":
      return "accent";
    case "pending":
      return "warn";
    case "rejected":
      return "error";
  }
}

export function describeScanReceipt(input: {
  outcome: ScanReceiptOutcome;
  method: ScanMethod;
  reason?: string;
}): ScanReceipt {
  const via = methodLabel(input.method);
  switch (input.outcome) {
    case "validated":
      return { tone: "ok", title: "✓ Checkpoint validated", detail: `Confirmed by the server via ${via}.`, method: input.method };
    case "synced":
      return { tone: "ok", title: "✓ Scan synced", detail: `Saved and uploaded via ${via}.`, method: input.method };
    case "queued-offline":
      return {
        tone: "pending",
        title: "Saved offline",
        detail: `Captured via ${via}. It will upload automatically when you're back online.`,
        method: input.method,
      };
    case "queued-retry":
      return {
        tone: "pending",
        title: "Saved — will retry",
        detail: input.reason
          ? `Captured via ${via}. Couldn't reach the server yet (${input.reason}); it stays saved and retries automatically.`
          : `Captured via ${via}. Couldn't reach the server yet; it stays saved and retries automatically.`,
        method: input.method,
      };
    case "flagged":
      return {
        tone: "pending",
        title: "Captured — flagged for review",
        detail: `Recorded via ${via}, but flagged for supervisor review.`,
        method: input.method,
      };
    case "duplicate":
      return {
        tone: "ok",
        title: "Already recorded",
        detail: `This checkpoint was already captured via ${via}; no action needed.`,
        method: input.method,
      };
    case "rejected":
      return {
        tone: "rejected",
        title: "Scan rejected",
        detail: `The server rejected this ${via} scan. Check with your supervisor.`,
        method: input.method,
      };
  }
}

function methodLabel(method: ScanMethod): string {
  if (method === "NFC") {
    return "NFC tag";
  }
  if (method === "QR") {
    return "QR code";
  }
  return "manual entry";
}
